// ============================================================================
// OBT F2-A — Kampanya oluşturma / dinamik soru tasarımcısı + yayın önizleme
// ============================================================================
// F2 Backend Enabler ile TAMAMLANDI:
//   • Taslak kampanya GERİ AÇILABİLİR (GET meta + GET questions) — hem meta hem
//     soru seti sunucudan yüklenir (talimat §3). NOT: backend `description`
//     alanını GET ile döndürmez; açıklama geri yüklenemez (kullanıcı uyarılır).
//   • Sorular ATOMİK PUT ile kaydedilir (yarım set oluşmaz); tasarımcı artık
//     yayından sonra KİLİTLENMEZ (talimat §4).
//   • Yayın öncesi backend PUBLICATION-PREVIEW çağrılır; `ok=false` ise yayın
//     düğmesi kapalıdır (talimat §6/§7).
//   • Sürüm çakışmasında (409) kullanıcıya güncel taslağı yeniden yükleme sunulur.
//   • `document` soru tipi yalnız yeni seçim için kapalıdır (upload ucu yok, §9).
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Send,
  Save,
  Info,
  Loader2,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { ObtPageHeader, ObtErrorState, ObtLoading } from '../ObtStates';
import QuestionEditor from './QuestionEditor';
import TargetPicker from './TargetPicker';
import { useTargetTree } from './useTargetTree';
import { useCampaignSubmit } from './useCampaignSubmit';
import { useCampaignDraft } from './useCampaignDraft';
import { validateForPublish } from './campaignValidation';
import {
  OBT_PARK_FEATURES,
  OBT_FILTER_MATCH,
  parkFeatureLabel,
  questionTypeLabel,
  roleLabel,
  previewIssueText,
} from './campaignEnums';

let questionCounter = 0;
function newQuestion() {
  questionCounter += 1;
  return {
    _key: `q_${questionCounter}`,
    question_text: '',
    question_type: 'boolean',
    is_required: true,
    photo_required: false,
    options: [],
  };
}

/**
 * @param {object} props
 * @param {string} props.personnelId       Aktör kimliği (auth kaynağından)
 * @param {string|null} [props.campaignId]  Verilirse taslak DÜZENLEME modu
 * @param {() => void} props.onDone         Liste ekranına dönüş
 */
export default function CampaignBuilder({
  personnelId,
  campaignId = null,
  initialTargetIds = null,
  onDone,
}) {
  const isEdit = Boolean(campaignId);

  const [basics, setBasics] = useState({
    title: '',
    description: '',
    topic: '',
    responseDueAt: '',
  });
  // "Geri çek ve düzenle" akışında önceki hedefler önceden seçili gelir; aday
  // listesi (chain) yüklendiğinde bu id'ler TargetPicker'da işaretli görünür.
  const [selectedTargets, setSelectedTargets] = useState(() =>
    Array.isArray(initialTargetIds) ? initialTargetIds : []
  );
  const [featureKeys, setFeatureKeys] = useState([]);
  const [featureMatch, setFeatureMatch] = useState('any');
  const [questions, setQuestions] = useState(() => (isEdit ? [] : [newQuestion()]));
  const [clientErrors, setClientErrors] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [conflict, setConflict] = useState(false);

  // Taslak yükleme (yalnız düzenleme modunda).
  const draft = useCampaignDraft(campaignId);

  const {
    candidates,
    loading: targetsLoading,
    error: targetsError,
    reload: reloadTargets,
  } = useTargetTree(personnelId);

  const {
    submitting,
    error: submitError,
    saveDraft,
    runPreview,
    confirmPublish,
    reset: resetSubmit,
  } = useCampaignSubmit();

  // Taslak yüklendiğinde form state'ini doldur + submit hook'unu senkronla.
  useEffect(() => {
    if (!isEdit || !draft.data) return;
    const d = draft.data;
    setBasics(d.basics);
    setQuestions(
      d.questions.length ? d.questions : [newQuestion()]
    );
    setFeatureKeys(d.featureKeys || []);
    setFeatureMatch(d.featureMatch || 'any');
    resetSubmit({ campaignId, version: d.version, basics: d.basics });
    setConflict(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, draft.data, campaignId]);

  // ---- Soru işlemleri ----
  const updateQuestion = (key, next) =>
    setQuestions((prev) => prev.map((q) => (q._key === key ? next : q)));
  const removeQuestion = (key) =>
    setQuestions((prev) =>
      prev.length > 1 ? prev.filter((q) => q._key !== key) : prev
    );
  const duplicateQuestion = (key) =>
    setQuestions((prev) => {
      const idx = prev.findIndex((q) => q._key === key);
      if (idx < 0) return prev;
      const src = prev[idx];
      const clone = {
        ...newQuestion(),
        question_text: src.question_text,
        question_type: src.question_type,
        is_required: src.is_required,
        photo_required: src.photo_required,
        options: (src.options || []).map((o) => ({ ...o })),
      };
      const copy = [...prev];
      copy.splice(idx + 1, 0, clone);
      return copy;
    });
  const moveQuestion = (index, delta) =>
    setQuestions((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const copy = [...prev];
      [copy[index], copy[target]] = [copy[target], copy[index]];
      return copy;
    });
  const addNewQuestion = () => setQuestions((prev) => [...prev, newQuestion()]);

  // ---- Hedef işlemleri ----
  const toggleTarget = (id) =>
    setSelectedTargets((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  const selectAllTargets = () =>
    setSelectedTargets(candidates.map((c) => c.personnelId));
  const clearTargets = () => setSelectedTargets([]);
  // Grup (rol) bazlı toplu seç/çıkar — kullanıcı bir rolün tamamını tek tıkla
  // işaretleyip sonra birkaçını çıkarabilsin (ör. 30 formen → hepsi → 5 çıkar).
  const selectManyTargets = (ids) =>
    setSelectedTargets((prev) => Array.from(new Set([...prev, ...ids])));
  const deselectManyTargets = (ids) => {
    const remove = new Set(ids);
    setSelectedTargets((prev) => prev.filter((x) => !remove.has(x)));
  };

  const toggleFeature = (key) =>
    setFeatureKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  // ---- Yayın için soru payload'ları (park filtresi her soruya iliştirilir) ----
  const filterConfiguration = useMemo(() => {
    if (featureKeys.length === 0) return null;
    return { feature_keys: featureKeys, match: featureMatch };
  }, [featureKeys, featureMatch]);

  // Atomik PUT için normalize edilmiş soru girdileri (display_order hook'ta atanır).
  const questionsForSubmit = useMemo(
    () =>
      questions.map((q) => ({
        question_text: q.question_text.trim(),
        question_type: q.question_type,
        is_required: q.is_required,
        photo_required: q.photo_required,
        options: (q.options || [])
          .filter((o) => o.option_label && o.option_label.trim())
          .map((o, i) => ({
            option_value: (o.option_value || o.option_label).trim(),
            option_label: o.option_label.trim(),
            display_order: i,
          })),
        ...(filterConfiguration ? { filter_configuration: filterConfiguration } : {}),
      })),
    [questions, filterConfiguration]
  );

  // İstemci doğrulaması için yalın soru görünümü.
  const questionsForValidation = useMemo(
    () =>
      questions.map((q) => ({
        question_text: q.question_text,
        question_type: q.question_type,
        options: q.options,
      })),
    [questions]
  );

  // ---- Aksiyonlar ----
  const handleReloadDraft = async () => {
    setConflict(false);
    await draft.reload();
    toast.info('Güncel taslak yeniden yüklendi.');
  };

  const handleSaveDraft = async () => {
    if (!basics.title.trim()) {
      setClientErrors(['Taslak kaydetmek için en az başlık gereklidir.']);
      return;
    }
    setClientErrors([]);
    const res = await saveDraft({ basics, questions: questionsForSubmit });
    if (res.ok) {
      toast.success('Taslak kaydedildi.');
      setConflict(false);
    } else if (res.conflict) {
      setConflict(true);
    }
  };

  const handleOpenPreview = async () => {
    const { ok, errors } = validateForPublish({
      basics,
      targets: selectedTargets,
      questions: questionsForValidation,
    });
    if (!ok) {
      setClientErrors(errors);
      toast.error('Yayınlamadan önce eksikleri giderin.');
      return;
    }
    setClientErrors([]);
    // Meta + sorular kaydet, sonra backend önizlemesi.
    const res = await runPreview({
      basics,
      questions: questionsForSubmit,
      targetIds: selectedTargets,
    });
    if (res.ok) {
      setPreviewData(res.preview);
      setPreviewOpen(true);
      setConflict(false);
    } else if (res.conflict) {
      setConflict(true);
    }
  };

  const handlePublish = async () => {
    // Preview çalışmadan / ok=false iken yayınlanamaz.
    if (!previewData || previewData.ok === false) return;
    const res = await confirmPublish({ targetIds: selectedTargets });
    setPreviewOpen(false);
    if (res.ok) {
      toast.success('Bilgi talebi yayınlandı.');
      onDone?.();
    } else if (res.conflict) {
      setConflict(true);
    }
  };

  const selectedCandidates = candidates.filter((c) =>
    selectedTargets.includes(c.personnelId)
  );

  // ---- Düzenleme modu yükleme/hata kapıları ----
  if (isEdit && draft.loading) {
    return (
      <div className="space-y-4">
        <BuilderTopBar onDone={onDone} disabled={false} label="Bilgi Talepleri" />
        <ObtLoading label="Taslak yükleniyor…" />
      </div>
    );
  }
  if (isEdit && draft.error) {
    return (
      <div className="space-y-4">
        <BuilderTopBar onDone={onDone} disabled={false} label="Bilgi Talepleri" />
        <ObtErrorState error={draft.error} onRetry={draft.reload} />
      </div>
    );
  }
  if (isEdit && draft.notEditable) {
    return (
      <div className="space-y-4">
        <BuilderTopBar onDone={onDone} disabled={false} label="Bilgi Talepleri" />
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <AlertTriangle className="h-8 w-8 text-amber-500" aria-hidden="true" />
            <p className="text-sm font-semibold text-amber-800">
              Bu bilgi talebi düzenlenemez
            </p>
            <p className="max-w-md text-sm text-amber-700">
              Yalnız taslak bilgi talepleri düzenlenebilir. Yayınlanan bilgi talebi
              değiştirilemez (immutable).
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BuilderTopBar onDone={onDone} disabled={submitting} label="Bilgi Talepleri" />

      <ObtPageHeader
        title={isEdit ? 'Taslağı Düzenle' : 'Yeni Bilgi Talebi'}
        description="Dinamik saha bilgi toplama talebi oluşturun. Yayınlanan bilgi talebi değiştirilemez."
      />

      {conflict && (
        <div
          role="alert"
          className="flex flex-col gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800 ring-1 ring-inset ring-red-200 sm:flex-row sm:items-center sm:justify-between"
        >
          <span className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            Bu bilgi talebi başka bir işlemle güncellendi. Güncel taslağı yeniden yükleyin.
          </span>
          {isEdit && (
            <Button variant="outline" size="sm" onClick={handleReloadDraft} disabled={submitting}>
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
              Güncel taslağı yükle
            </Button>
          )}
        </div>
      )}

      {isEdit && draft.descriptionUnavailable && (
        <div
          role="note"
          className="flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-xs text-blue-800 ring-1 ring-inset ring-blue-200"
        >
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>
            Açıklama alanı sunucudan geri yüklenemiyor. Değiştirmek isterseniz
            yeniden yazın; boş bırakırsanız mevcut açıklama korunur.
          </span>
        </div>
      )}

      {/* 1. Temel Bilgiler */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">1. Temel Bilgiler</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="c-title">Bilgi talebi başlığı *</Label>
            <Input
              id="c-title"
              value={basics.title}
              maxLength={200}
              placeholder="Örn: Havuzların çalışma durumu tespiti"
              onChange={(e) => setBasics((b) => ({ ...b, title: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-due">Son cevap tarihi</Label>
            <Input
              id="c-due"
              type="datetime-local"
              value={basics.responseDueAt}
              onChange={(e) =>
                setBasics((b) => ({ ...b, responseDueAt: e.target.value }))
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-desc">Açıklama / talimat</Label>
            <Textarea
              id="c-desc"
              value={basics.description}
              maxLength={8000}
              placeholder="Bilgi talebine dair açıklama veya saha talimatı"
              onChange={(e) =>
                setBasics((b) => ({ ...b, description: e.target.value }))
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* 2. Hedef Kitle */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">2. Hedef Kitle</CardTitle>
        </CardHeader>
        <CardContent>
          <TargetPicker
            candidates={candidates}
            loading={targetsLoading}
            error={targetsError}
            selectedIds={selectedTargets}
            onToggle={toggleTarget}
            onSelectAll={selectAllTargets}
            onClear={clearTargets}
            onSelectMany={selectManyTargets}
            onDeselectMany={deselectManyTargets}
            onReload={reloadTargets}
          />
        </CardContent>
      </Card>

      {/* 3. Park Özelliği Filtresi */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">3. Park Özelliği Filtresi</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Seçilen park özelliğine sahip parklar Formen ekranında otomatik
            listelenecektir. Envanterde görünmeyen ancak sahada mevcut olan
            parklar ayrıca önerilebilir.
          </p>
          <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
            {OBT_PARK_FEATURES.map((f) => (
              <label
                key={f.key}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50"
              >
                <Checkbox
                  checked={featureKeys.includes(f.key)}
                  onCheckedChange={() => toggleFeature(f.key)}
                  aria-label={`${f.label} özelliği`}
                />
                <span className="text-sm text-gray-800">{f.label}</span>
              </label>
            ))}
          </div>
          {featureKeys.length > 1 && (
            <div className="max-w-md space-y-1.5">
              <Label htmlFor="c-match">Eşleşme kuralı</Label>
              <Select value={featureMatch} onValueChange={setFeatureMatch}>
                <SelectTrigger id="c-match">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OBT_FILTER_MATCH.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 4. Sorular */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base">4. Sorular</CardTitle>
          <Button type="button" variant="outline" size="sm" onClick={addNewQuestion}>
            <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
            Soru ekle
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {questions.map((q, index) => (
            <QuestionEditor
              key={q._key}
              question={q}
              index={index}
              isFirst={index === 0}
              isLast={index === questions.length - 1}
              onChange={updateQuestion}
              onRemove={removeQuestion}
              onDuplicate={duplicateQuestion}
              onMove={moveQuestion}
            />
          ))}
        </CardContent>
      </Card>

      {/* Hata özeti */}
      {clientErrors.length > 0 && (
        <div
          role="alert"
          className="rounded-lg bg-red-50 p-4 text-sm text-red-800 ring-1 ring-inset ring-red-200"
        >
          <p className="mb-1 font-semibold">Lütfen aşağıdakileri düzeltin:</p>
          <ul className="list-inside list-disc space-y-0.5">
            {clientErrors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {submitError && !conflict && <ObtErrorState error={submitError} />}

      {/* Aksiyon çubuğu */}
      <div className="sticky bottom-0 flex flex-col gap-2 border-t border-gray-200 bg-white/95 py-3 backdrop-blur sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={handleSaveDraft}
          disabled={submitting}
        >
          {submitting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          Taslak Kaydet
        </Button>
        <Button type="button" onClick={handleOpenPreview} disabled={submitting}>
          {submitting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Send className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          Önizle ve Yayınla
        </Button>
      </div>

      {/* Yayın önizleme + onay diyaloğu (backend preview verisiyle) */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Yayın önizlemesi</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <Row label="Bilgi Talebi" value={basics.title || '—'} />
            {basics.description?.trim() && (
              <div className="rounded-md bg-gray-50 p-2">
                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                  Açıklama / talimat
                </p>
                <p className="whitespace-pre-wrap text-sm text-gray-700">
                  {basics.description.trim()}
                </p>
              </div>
            )}
            <div>
              <Row
                label="Hedef kişi"
                value={`${previewData?.target_count ?? selectedTargets.length} kişi`}
              />
              {/* Kim(ler)e gönderiliyor — sayı değil, isimler de görünsün. */}
              {selectedCandidates.length > 0 && (
                <ul className="mt-0.5 space-y-0.5 pl-1 text-xs text-gray-600">
                  {selectedCandidates.map((c) => (
                    <li key={c.personnelId}>
                      • {c.name || 'İsimsiz personel'}{' '}
                      <span className="text-gray-400">({roleLabel(c.roleSlug)})</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Row
              label="Park özelliği"
              value={
                featureKeys.length
                  ? featureKeys.map(parkFeatureLabel).join(', ')
                  : 'Filtre yok'
              }
            />
            <Row
              label="Son cevap tarihi"
              value={
                basics.responseDueAt
                  ? new Date(basics.responseDueAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })
                  : 'Süresiz'
              }
            />

            {/* Doldurulan SORULAR — önizlemenin özü: ne sorulacağı bir arada. */}
            <div className="rounded-md border border-gray-200 p-2">
              <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                Sorular ({previewData?.question_count ?? questions.length})
              </p>
              {questions.length === 0 ? (
                <p className="text-xs text-gray-500">Henüz soru eklenmedi.</p>
              ) : (
                <ol className="space-y-1.5">
                  {questions.map((q, i) => {
                    const opts = (q.options || [])
                      .map((o) => (o.option_value || o.option_label || '').trim())
                      .filter(Boolean);
                    return (
                      <li key={q._key || i} className="text-sm text-gray-800">
                        <span className="mr-1 font-medium text-gray-500">
                          {i + 1}.
                        </span>
                        {q.question_text?.trim() || '(boş soru)'}
                        <span className="ml-1 text-xs text-gray-400">
                          ({questionTypeLabel(q.question_type)}
                          {q.is_required ? ', zorunlu' : ''}
                          {q.photo_required ? ', fotoğraf zorunlu' : ''})
                        </span>
                        {opts.length > 0 && (
                          <span className="block pl-5 text-xs text-gray-500">
                            Seçenekler: {opts.join(' / ')}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            {selectedCandidates.some((c) => c.roleSlug === 'formen') && (
              <p className="rounded-md bg-blue-50 p-2 text-xs text-blue-800">
                Doğrudan Formen hedefleniyor; ilgili üst amirler bilgi talebini takip
                edebilir.
              </p>
            )}

            {previewData && previewData.ok === false && (
              <div
                role="alert"
                className="rounded-md bg-red-50 p-3 text-xs text-red-800 ring-1 ring-inset ring-red-200"
              >
                <p className="mb-1 font-semibold">
                  Yayınlanamaz — önce şu sorunları giderin:
                </p>
                <ul className="list-inside list-disc space-y-0.5">
                  {(previewData.issues || []).map((iss, i) => (
                    <li key={i}>{previewIssueText(iss)}</li>
                  ))}
                  {(previewData.issues || []).length === 0 && (
                    <li>Yayın ön koşulları sağlanmıyor.</li>
                  )}
                </ul>
              </div>
            )}
            <p className="pt-1 text-xs text-gray-500">
              Yayınlanan bilgi talebi değiştirilemez.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPreviewOpen(false)}
              disabled={submitting}
            >
              Vazgeç
            </Button>
            <Button
              onClick={handlePublish}
              disabled={submitting || !previewData || previewData.ok === false}
            >
              {submitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              Yayınla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BuilderTopBar({ onDone, disabled, label }) {
  return (
    <div className="flex items-center gap-3">
      <Button variant="ghost" size="sm" onClick={onDone} disabled={disabled}>
        <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
        {label}
      </Button>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-gray-500">{label}</span>
      <span className="text-right font-medium text-gray-900">{value}</span>
    </div>
  );
}
